import React, { useRef, useEffect } from 'react';
import {
  Bold, Italic, Underline, Strikethrough, List, ListOrdered, Quote, Code,
  Link2, Table as TableIcon, Heading1, Heading2, Heading3, Pilcrow
} from 'lucide-react';
import './cms-editor.css';

const exec = (cmd, val = null) => document.execCommand(cmd, false, val);

const Btn = ({ onClick, title, testid, children }) => (
  <button
    type="button"
    title={title}
    data-testid={testid}
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    className="p-2 rounded text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
  >
    {children}
  </button>
);

const Divider = () => <span className="w-px h-5 bg-slate-700 mx-1" />;

// Extensible, dependency-free rich text editor (contentEditable). A future
// Media Library can plug in by inserting nodes via exec('insertHTML', ...).
const RichTextEditor = ({ value, onChange, placeholder = 'Start writing…' }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) ref.current.innerHTML = value || '';
    // Initialize once on mount (or remount via key). Avoids cursor resets.
    // eslint-disable-next-line
  }, []);

  const emit = () => onChange?.(ref.current?.innerHTML || '');
  const block = (tag) => { exec('formatBlock', tag); emit(); };

  const insertTable = () => {
    const rows = 2;
    const cols = 2;
    let html = '<table class="cms-table"><tbody>';
    for (let r = 0; r < rows; r++) {
      html += '<tr>';
      for (let c = 0; c < cols; c++) html += '<td>Cell</td>';
      html += '</tr>';
    }
    html += '</tbody></table><p><br/></p>';
    exec('insertHTML', html);
    emit();
  };

  const addLink = () => {
    const url = window.prompt('Enter link URL (https://…)');
    if (url) { exec('createLink', url); emit(); }
  };

  return (
    <div className="border border-slate-700 rounded-lg overflow-hidden bg-slate-900/50" data-testid="cms-rich-editor">
      <div className="flex flex-wrap items-center gap-0.5 p-2 border-b border-slate-700 bg-slate-800/60">
        <Btn title="Heading 1" testid="rte-h1" onClick={() => block('H1')}><Heading1 className="w-4 h-4" /></Btn>
        <Btn title="Heading 2" testid="rte-h2" onClick={() => block('H2')}><Heading2 className="w-4 h-4" /></Btn>
        <Btn title="Heading 3" testid="rte-h3" onClick={() => block('H3')}><Heading3 className="w-4 h-4" /></Btn>
        <Btn title="Paragraph" testid="rte-p" onClick={() => block('P')}><Pilcrow className="w-4 h-4" /></Btn>
        <Divider />
        <Btn title="Bold" testid="rte-bold" onClick={() => { exec('bold'); emit(); }}><Bold className="w-4 h-4" /></Btn>
        <Btn title="Italic" testid="rte-italic" onClick={() => { exec('italic'); emit(); }}><Italic className="w-4 h-4" /></Btn>
        <Btn title="Underline" testid="rte-underline" onClick={() => { exec('underline'); emit(); }}><Underline className="w-4 h-4" /></Btn>
        <Btn title="Strikethrough" testid="rte-strike" onClick={() => { exec('strikeThrough'); emit(); }}><Strikethrough className="w-4 h-4" /></Btn>
        <Divider />
        <Btn title="Bullet list" testid="rte-ul" onClick={() => { exec('insertUnorderedList'); emit(); }}><List className="w-4 h-4" /></Btn>
        <Btn title="Numbered list" testid="rte-ol" onClick={() => { exec('insertOrderedList'); emit(); }}><ListOrdered className="w-4 h-4" /></Btn>
        <Btn title="Quote" testid="rte-quote" onClick={() => block('BLOCKQUOTE')}><Quote className="w-4 h-4" /></Btn>
        <Btn title="Code block" testid="rte-code" onClick={() => block('PRE')}><Code className="w-4 h-4" /></Btn>
        <Divider />
        <Btn title="Insert link" testid="rte-link" onClick={addLink}><Link2 className="w-4 h-4" /></Btn>
        <Btn title="Insert table" testid="rte-table" onClick={insertTable}><TableIcon className="w-4 h-4" /></Btn>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        data-testid="cms-rich-editor-area"
        data-placeholder={placeholder}
        className="cms-content min-h-[280px] max-h-[520px] overflow-y-auto p-4 text-slate-100 focus:outline-none"
      />
    </div>
  );
};

export default RichTextEditor;
